import React, { useEffect, useRef, useState, useCallback } from 'react';

/** File extensions we treat as playable video. Lower-case, no leading dot. */
const VIDEO_EXTS = ['mp4', 'webm', 'mov', 'm4v', 'ogv', 'ogg', 'avi', 'mkv'];

/** True when the artifact filename or MIME-type string indicates a video. */
export const isVideoArtifact = (filenameOrType?: string): boolean => {
  if (!filenameOrType) return false;
  const lower = filenameOrType.toLowerCase();
  if (lower.startsWith('video/')) return true;
  const ext = lower.split('.').pop() || '';
  return VIDEO_EXTS.includes(ext);
};

interface AuthenticatedVideoProps {
  src: string;
  accessToken: string;
  className?: string;
  /** Show native playback controls. Defaults to true. */
  controls?: boolean;
  /** Auto-play (will be muted to satisfy browser autoplay policies). */
  autoPlay?: boolean;
  /** Loop playback. Useful for thumbnail previews. */
  loop?: boolean;
  /** Muted playback. Required alongside autoPlay. */
  muted?: boolean;
  /** Fired when fetch/load fails. */
  onError?: () => void;
}

/**
 * Authenticated <video> element.
 *
 * Mirrors AuthenticatedImage: fetches the artifact with the bearer token,
 * stashes it in an object URL, and wires that URL to a <video> element.
 * We can't use a raw `src` because the API requires an Authorization header.
 *
 * On iOS/mobile, generates a poster from the first frame since `preload="metadata"`
 * often shows a black frame.
 */
export const AuthenticatedVideo: React.FC<AuthenticatedVideoProps> = ({
  src,
  accessToken,
  className = '',
  controls = true,
  autoPlay = false,
  loop = false,
  muted = false,
  onError,
}) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [posterUrl, setPosterUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Generate poster from first frame once video metadata loads
  const generatePoster = useCallback(() => {
    const video = videoRef.current;
    if (!video || video.videoWidth === 0 || video.videoHeight === 0) return;

    try {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        setPosterUrl(dataUrl);
      }
    } catch (err) {
      // Canvas capture may fail due to CORS — silently ignore
      console.warn('Could not generate video poster:', err);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    let objectUrl: string | null = null;

    const fetchVideo = async () => {
      try {
        setLoading(true);
        setError(false);

        const response = await fetch(src, {
          method: 'GET',
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);

        if (isMounted) {
          setBlobUrl(objectUrl);
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed to load authenticated video:', err);
        if (isMounted) {
          setError(true);
          setLoading(false);
          onError?.();
        }
      }
    };

    if (src && accessToken) fetchVideo();

    return () => {
      isMounted = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src, accessToken, onError]);

  // Handle video loaded — seek to first frame and capture poster
  const handleLoadedData = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    // Seek to a tiny offset to ensure first frame is decoded
    video.currentTime = 0.1;
  }, []);

  const handleSeeked = useCallback(() => {
    generatePoster();
    // Reset to beginning after capturing
    const video = videoRef.current;
    if (video && !autoPlay) {
      video.currentTime = 0;
    }
  }, [generatePoster, autoPlay]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 ${className}`}>
        <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
    );
  }

  if (error || !blobUrl) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 ${className}`}>
        <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
    );
  }

  return (
    <video
      ref={videoRef}
      src={blobUrl}
      poster={posterUrl || undefined}
      className={className}
      controls={controls}
      autoPlay={autoPlay}
      loop={loop}
      muted={muted || autoPlay}
      playsInline
      preload="metadata"
      onLoadedData={handleLoadedData}
      onSeeked={handleSeeked}
    />
  );
};

export default AuthenticatedVideo;
